/**
 * Shareable admin state links.
 *
 * A link carries a base64url-encoded script of `adminHelp()` commands in the
 * `?admin=` query param; on load the app replays them in order, so a single URL
 * can reproduce a whole state (logged in / anonymous, plan, used words, limit
 * overrides, an open modal, …) instead of a list of console commands.
 *
 *   setPlan('Premium'); setUsedWords(9500); showModal('increase-limits-form')
 *
 * This module holds only the pure parts — codec, parser and the runner — so the
 * command registry (which needs the stores) can live in `adminUtil.ts` and this
 * file stays unit-testable.
 */

import {observable, runInAction} from 'mobx';

import {ADMIN_SECRET, IS_PROD} from 'config';

/** Query param carrying the encoded script. */
export const ADMIN_PARAM = 'admin';

/** A single parsed command: its name and already-decoded arguments. */
export interface AdminCall {
    name: string;
    args: unknown[];
}

export interface AdminCommandSpec {
    run: (...args: any[]) => unknown | Promise<unknown>;
    /** Calls the backend as the current user — the runner waits for a token first. */
    needsToken?: boolean;
    /** Mutates server-side state — the store is refreshed before the next UI command. */
    mutatesServer?: boolean;
    /** Changes identity (login/logout/delete) — the page reloads and the queue resumes after. */
    reload?: boolean;
    /** One line for `adminHelp()`, including the signature. */
    help: string;
}

export type AdminRegistry = Record<string, AdminCommandSpec>;

export interface AdminRunnerHooks {
    /** Resolves once the app has an auth token (or gives up — the command then fails loudly). */
    waitForToken: () => Promise<void>;
    /** Re-reads the user from the backend after server-side mutations. */
    refreshState: () => Promise<void>;
    reload: () => void;
    /** Persists the not-yet-executed tail across a reload. */
    saveQueue: (calls: AdminCall[]) => void;
    clearQueue: () => void;
}

export interface AdminRunResult {
    /** False when the script aborted early (unknown command or a failing one). */
    ok: boolean;
    /** Commands actually executed (a reload counts the command that triggered it). */
    executed: number;
    error?: string;
    /** True when the run handed off to a page reload — the tail resumes after it. */
    reloading?: boolean;
}

/** Console prefix for everything the link machinery says. */
export const LOG_PREFIX = '[admin-link]';

// --- base64url codec (UTF-8 safe, no TextEncoder — jsdom/older Safari lack it) ---

const utf8ToBinary = (text: string): string =>
    encodeURIComponent(text).replace(/%([0-9A-F]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

const binaryToUtf8 = (binary: string): string =>
    decodeURIComponent(
        Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );

/** Encode a command script into the `?admin=` value. */
export const encodeAdminScript = (script: string): string =>
    btoa(utf8ToBinary(script)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** Decode an `?admin=` value back into the command script. Throws on malformed input. */
export const decodeAdminScript = (encoded: string): string => {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return binaryToUtf8(atob(padded));
};

// --- script parsing ---

/** Read a single- or double-quoted string literal, returning its value and the index after it. */
const readString = (src: string, start: number): { value: string; next: number } => {
    const quote = src[start];
    let value = '';
    let i = start + 1;
    while (i < src.length) {
        const char = src[i];
        if (char === '\\') {
            const escaped = src[i + 1];
            switch (escaped) {
                case 'n': value += '\n'; break;
                case 't': value += '\t'; break;
                case 'r': value += '\r'; break;
                case 'u': {
                    // Validate before advancing: an unchecked parseInt yields NaN ->
                    // a silent NUL in the text, and a short escape (`\u12'`) would move
                    // the cursor past the closing quote, so the "string" swallows the
                    // rest of the script and the error points nowhere near the typo.
                    const hex = src.slice(i + 2, i + 6);
                    if (!/^[0-9a-fA-F]{4}$/.test(hex)) {
                        throw new Error(`bad \\u escape in: ${src}`);
                    }
                    value += String.fromCharCode(parseInt(hex, 16));
                    i += 4;
                    break;
                }
                default: value += escaped;
            }
            i += 2;
            continue;
        }
        if (char === quote) return {value, next: i + 1};
        value += char;
        i++;
    }
    throw new Error(`unterminated string in: ${src}`);
};

/**
 * Turn hand-written JS-ish arguments into strict JSON: single quotes become double
 * quotes, bare object keys get quoted and trailing commas are dropped. Links are
 * meant to be readable and copy-pasteable from the console, where nobody types JSON.
 */
const relaxToJson = (src: string): string => {
    let out = '';
    let i = 0;
    while (i < src.length) {
        const char = src[i];
        if (char === '"' || char === "'") {
            const {value, next} = readString(src, i);
            out += JSON.stringify(value);
            i = next;
            continue;
        }
        if (/[A-Za-z_$]/.test(char)) {
            let end = i;
            while (end < src.length && /[\w$]/.test(src[end])) end++;
            const word = src.slice(i, end);
            let after = end;
            while (after < src.length && /\s/.test(src[after])) after++;
            // A bare word before ':' is an object key; anything else (true/false/null) is a literal.
            out += src[after] === ':' ? JSON.stringify(word) : word;
            i = end;
            continue;
        }
        if (char === ',') {
            let after = i + 1;
            while (after < src.length && /\s/.test(src[after])) after++;
            if (src[after] === '}' || src[after] === ']') {
                i++; // trailing comma
                continue;
            }
        }
        out += char;
        i++;
    }
    return out;
};

const parseArgs = (src: string): unknown[] => {
    const trimmed = src.trim();
    if (!trimmed) return [];
    try {
        return JSON.parse(`[${trimmed}]`);
    } catch {
        // fall through to the relaxed pass
    }
    try {
        return JSON.parse(`[${relaxToJson(trimmed)}]`);
    } catch {
        throw new Error(`cannot parse arguments: ${src}`);
    }
};

/** Split a script into statements on `;` / newlines that are not inside strings or brackets. */
const splitStatements = (script: string): string[] => {
    const statements: string[] = [];
    let current = '';
    let depth = 0;
    let i = 0;
    while (i < script.length) {
        const char = script[i];
        if (char === '"' || char === "'") {
            const {next} = readString(script, i);
            current += script.slice(i, next);
            i = next;
            continue;
        }
        if (char === '(' || char === '[' || char === '{') depth++;
        if (char === ')' || char === ']' || char === '}') depth--;
        if ((char === ';' || char === '\n') && depth <= 0) {
            statements.push(current);
            current = '';
            i++;
            continue;
        }
        current += char;
        i++;
    }
    statements.push(current);
    return statements.map((statement) => statement.trim()).filter(Boolean);
};

const CALL_RE = /^([A-Za-z_$][\w$]*)\s*(?:\(([\s\S]*)\))?$/;

/**
 * Parse a command script into calls. Accepts both the console-style form
 * (`setPlan('Premium'); showModal('x')`) and a JSON array of `["name", ...args]`
 * tuples, which is easier to generate programmatically.
 */
export const parseAdminScript = (script: string): AdminCall[] => {
    const trimmed = script.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith('[')) {
        const parsed = JSON.parse(trimmed);
        if (!Array.isArray(parsed)) throw new Error('JSON script must be an array of calls');
        return parsed.map((entry: unknown) => {
            if (Array.isArray(entry)) {
                const [name, ...args] = entry;
                if (typeof name !== 'string') throw new Error(`invalid call: ${JSON.stringify(entry)}`);
                return {name, args};
            }
            if (entry && typeof entry === 'object') {
                const {name, args} = entry as { name?: unknown; args?: unknown };
                if (typeof name !== 'string') throw new Error(`invalid call: ${JSON.stringify(entry)}`);
                return {name, args: Array.isArray(args) ? args : args === undefined ? [] : [args]};
            }
            if (typeof entry === 'string') return {name: entry, args: []};
            throw new Error(`invalid call: ${JSON.stringify(entry)}`);
        });
    }

    return splitStatements(trimmed).map((statement) => {
        const match = CALL_RE.exec(statement);
        if (!match) throw new Error(`cannot parse command: ${statement}`);
        return {name: match[1], args: parseArgs(match[2] ?? '')};
    });
};

// --- running ---

/**
 * Execute parsed calls in order.
 *
 * Server mutations are batched: the store is refreshed lazily, right before the
 * first UI command that would read the new state (and at the end), so a link with
 * five mutations still costs one refresh. A command flagged `reload` hands the
 * remaining calls to `saveQueue` and reloads — the queue resumes on the next load.
 */
export const runAdminCalls = async (
    calls: AdminCall[],
    registry: AdminRegistry,
    hooks: AdminRunnerHooks,
): Promise<AdminRunResult> => {
    let pendingRefresh = false;
    let executed = 0;

    for (let index = 0; index < calls.length; index++) {
        const call = calls[index];
        const spec = registry[call.name];

        if (!spec) {
            hooks.clearQueue();
            const error = `unknown command: ${call.name}`;
            console.error(`${LOG_PREFIX} ${error}`);
            return {ok: false, executed, error};
        }

        try {
            // Inside the try: waiting for a token can time out, and that has to abort the
            // script the same way a failing command does — not escape as an unhandled rejection.
            if (spec.needsToken) await hooks.waitForToken();

            // A UI command must see the state the mutations before it produced.
            if (!spec.mutatesServer && pendingRefresh) {
                await hooks.refreshState();
                pendingRefresh = false;
            }

            console.log(`${LOG_PREFIX} ${call.name}`, ...call.args);
            await spec.run(...call.args);
            executed++;
        } catch (e) {
            hooks.clearQueue();
            const error = `${call.name} failed: ${e instanceof Error ? e.message : String(e)}`;
            console.error(`${LOG_PREFIX} ${error}`, e);
            return {ok: false, executed, error};
        }

        if (spec.mutatesServer) pendingRefresh = true;

        if (spec.reload) {
            hooks.saveQueue(calls.slice(index + 1));
            hooks.reload();
            return {ok: true, executed, reloading: true};
        }
    }

    if (pendingRefresh) await hooks.refreshState();
    hooks.clearQueue();
    return {ok: true, executed};
};

// --- capturing the param ---

/**
 * Where replaying a link is allowed: a locally built bundle (only those carry the
 * secret in the env) plus the dev hosts, where the secret is pasted by hand.
 *
 * A production build never replays, whatever its host or env happens to carry — a
 * link that mutates the account of whoever opens it has no business there, and that
 * shouldn't hinge on `REACT_APP_ADMIN_SECRET` staying absent from `prod.env`.
 */
export const isAdminLinkAllowed = (
    hostname: string = window.location.hostname,
    hasSecret: boolean = Boolean(ADMIN_SECRET),
    isProdBuild: boolean = IS_PROD,
): boolean => {
    if (isProdBuild) return false;
    return (
        hasSecret ||
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.endsWith('.localhost') ||
        hostname === 'dev.its-ai.org'
    );
};

/** Key holding the in-flight run (which script, and what is left of it). */
export const RUN_STATE_KEY = 'IA__admin_link_run';

/** A run older than this is stale — a tab left open, not a script mid-flight. */
export const RUN_STATE_TTL_MS = 5 * 60 * 1000;

/** The script a tab is currently replaying, plus the tail still waiting on a reload. */
export interface AdminRunState {
    /** The encoded script, so a re-appearing `?admin=` can be recognised as the same run. */
    script: string;
    /** Commands not executed yet; null until a reload-triggering command parks them. */
    calls: AdminCall[] | null;
    startedAt: number;
}

// Storage itself can throw — blocked cookies, partitioned or disabled storage,
// Safari private mode. `captureAdminScript` runs synchronously in the app entry
// for every visitor, so an unguarded access here would turn "the admin link does
// nothing" into "the app does not boot" (the same reason `persistence.ts` wraps
// its reads).
const safely = <T>(fn: () => T, fallback: T): T => {
    try {
        return fn();
    } catch {
        return fallback;
    }
};

/** The run this tab is replaying, or null when there is none (or it went stale). */
export const readRunState = (
    storage: Storage = window.sessionStorage,
    now: number = Date.now(),
): AdminRunState | null => safely(() => {
    const raw = storage.getItem(RUN_STATE_KEY);
    if (!raw) return null;
    try {
        const state = JSON.parse(raw) as AdminRunState;
        if (typeof state?.script !== 'string') return null;
        if (now - state.startedAt > RUN_STATE_TTL_MS) {
            storage.removeItem(RUN_STATE_KEY);
            return null;
        }
        return state;
    } catch {
        storage.removeItem(RUN_STATE_KEY);
        return null;
    }
}, null);

/** Record the run in flight, so a re-appearing `?admin=` is not replayed twice. */
export const writeRunState = (state: AdminRunState, storage: Storage = window.sessionStorage): void =>
    safely(() => storage.setItem(RUN_STATE_KEY, JSON.stringify(state)), undefined);

/** Forget the run — the script finished, or aborted. */
export const clearRunState = (storage: Storage = window.sessionStorage): void =>
    safely(() => storage.removeItem(RUN_STATE_KEY), undefined);

/** Take `?admin=` out of the current URL, leaving every other param alone. */
export const stripAdminParam = (): void => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(ADMIN_PARAM)) return;
    params.delete(ADMIN_PARAM);
    const search = params.toString();
    window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
    );
};

/**
 * Whether a link is still being replayed. Set synchronously by `captureAdminScript`
 * — before the first render — so the app can hold the UI behind its loading overlay
 * until every command has landed. Otherwise the page paints the state the account
 * was in a moment ago (the old word count, the previous plan) and then jumps.
 */
const running = observable.box(false);

export const isAdminLinkRunning = (): boolean => running.get();

export const endAdminRun = (): void => {
    if (holdTimer !== undefined) clearTimeout(holdTimer);
    holdTimer = undefined;
    runInAction(() => running.set(false));
};

/** A held UI that never lifts is worse than one that shows the state early. */
export const MAX_HOLD_MS = 30_000;

let holdTimer: ReturnType<typeof setTimeout> | undefined;

const beginAdminRun = (): void => {
    runInAction(() => running.set(true));
    if (holdTimer !== undefined) clearTimeout(holdTimer);
    holdTimer = setTimeout(() => {
        console.warn(`${LOG_PREFIX} still running after ${MAX_HOLD_MS / 1000}s — showing the app anyway`);
        endAdminRun();
    }, MAX_HOLD_MS);
};

let capturedScript: string | null = null;

/**
 * Consume `?admin=` before anything else can touch the query.
 *
 * Runs in the app's synchronous entry because the runner lives in a lazily-loaded
 * chunk that arrives well after the first navigations — by then the scan page has
 * mirrored its own state into the query and the param may be gone.
 *
 * The param can also come *back*: the bare-route language redirect navigates to a
 * location snapshotted before this ran, so `/?admin=…` reappears as `/en/?admin=…`.
 * A run is therefore recorded in sessionStorage and the same script is captured only
 * once — otherwise the reload that `login()` and friends trigger would re-run that
 * identity command and throw away the queued rest of the script.
 */
export const captureAdminScript = (): void => {
    // Gate first, and unconditionally: where links are not allowed this function has
    // to be a true no-op — no storage read, no run flag. Guarding only the `?admin=`
    // branch left the parked-tail branch below able to blur a production app on the
    // strength of a stale sessionStorage key, and made every prod page load touch
    // storage synchronously before React mounts.
    if (!isAdminLinkAllowed()) {
        if (new URLSearchParams(window.location.search).has(ADMIN_PARAM)) {
            console.warn(`${LOG_PREFIX} ignoring ?${ADMIN_PARAM}= — admin links only run locally and on dev`);
            // Drop it anyway: an ignored script has no business staying in the address
            // bar, in what the user copies from it, or in the analytics page_location.
            stripAdminParam();
        }
        return;
    }

    const encoded = new URLSearchParams(window.location.search).get(ADMIN_PARAM);
    const state = readRunState();

    if (!encoded) {
        // No link, but a tail parked by a reload-triggering command: that run is still
        // in flight, so the UI has to stay held until the runner finishes it.
        if (state?.calls?.length) beginAdminRun();
        return;
    }

    // Same script as the run already in flight: this is the redirect putting it back,
    // or a reload in the middle of the script. Clean the URL and let the queue continue.
    if (state?.script === encoded) {
        if (state.calls?.length) {
            beginAdminRun();
        } else {
            // Nothing parked, so there is nothing left to replay — the run already
            // finished, or the tab navigated away mid-script. Say so instead of
            // silently swallowing the link (and don't raise the overlay for it).
            console.warn(
                `${LOG_PREFIX} this link already ran in this tab; it replays again in ` +
                `${RUN_STATE_TTL_MS / 60000} min, or right away in a new tab`,
            );
        }
        stripAdminParam();
        return;
    }

    beginAdminRun();
    capturedScript = encoded;
    writeRunState({script: encoded, calls: null, startedAt: Date.now()});
    stripAdminParam();
};

/** Hand the captured script to the runner (once). */
export const takeCapturedScript = (): string | null => {
    const script = capturedScript;
    capturedScript = null;
    return script;
};

/** Build a shareable link for a script (defaults to the current page). */
export const buildAdminUrl = (script: string, base: string = window.location.href): string => {
    const url = new URL(base, window.location.href);
    url.searchParams.set(ADMIN_PARAM, encodeAdminScript(script));
    return url.toString();
};
